/**
 * What the agents app asks the server beside what the screens from before already ask
 * (those calls live in src/lib/api.ts and are used as they are).
 */
import type { AgentRecord, AgentsOverview, GroupRecord, RoutineRecord, WorkspaceRecord } from '@shared/agents'
import type { Attachment, Chat, ChatMessage, ChatMode, ChatStreamEvent, Memory, MemoryKind } from '@shared/types'

export * from '../lib/api'

async function j<T>(res: Response): Promise<T> {
  if (res.status === 401 && !res.url.includes('/api/auth/')) window.dispatchEvent(new CustomEvent('lb-unauthorized'))
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`
    try {
      const body = await res.json()
      if (body?.error) msg = body.error
      else if (Array.isArray(body?.errors) && body.errors.length) msg = body.errors.join(' · ')
    } catch {
      /* not json */
    }
    throw new Error(msg)
  }
  return (await res.json()) as T
}
const send = (method: string, body?: unknown): RequestInit => ({ method, headers: body === undefined ? undefined : { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) })
const q = (p: Record<string, string | number | undefined | null>) => {
  const s = Object.entries(p)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&')
  return s ? `?${s}` : ''
}

/** A conversation as a list shows it: where it belongs, the line under its title, and whether it is being answered. */
export type ChatInfo = Chat & { workspaceId?: string | null; preview?: string; busy?: boolean }

export const agentsApi = {
  overview: (ws?: string | null) => fetch(`/api/agents${q({ ws })}`).then((r) => j<AgentsOverview>(r)),
  create: (a: { name: string; label?: string; description?: string; palette?: string; lang?: string | null; ws?: string }) => fetch('/api/agents', send('POST', a)).then((r) => j<AgentRecord>(r)),
  patch: (id: string, p: Partial<Pick<AgentRecord, 'name' | 'label' | 'description' | 'palette' | 'lang' | 'notify' | 'pinned' | 'hidden' | 'folderId'>>) => fetch(`/api/agents/${id}`, send('PATCH', p)).then((r) => j<AgentRecord>(r)),
  remove: (id: string) => fetch(`/api/agents/${id}`, send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
  chats: (id: string) => fetch(`/api/agents/${id}/chats`).then((r) => j<ChatInfo[]>(r)),
  search: (id: string, text: string) => fetch(`/api/agents/${id}/search${q({ q: text })}`).then((r) => j<{ chatId: string; title: string; snippet: string; at: number }[]>(r)),
  routines: () => fetch('/api/agents/routines').then((r) => j<RoutineRecord[]>(r)),

  addFolder: (name: string, members: string[], ws?: string) => fetch('/api/agents/folders', send('POST', { name, members, ws })).then((r) => j<{ id: string }>(r)),
  renameFolder: (id: string, name: string) => fetch(`/api/agents/folders/${id}`, send('PATCH', { name })).then((r) => j<{ ok: boolean }>(r)),
  removeFolder: (id: string) => fetch(`/api/agents/folders/${id}`, send('DELETE')).then((r) => j<{ ok: boolean }>(r)),

  addGroup: (name: string, members: string[], ws?: string) => fetch('/api/agents/groups', send('POST', { name, members, ws })).then((r) => j<GroupRecord>(r)),
  patchGroup: (id: string, p: { name?: string; members?: string[] }) => fetch(`/api/agents/groups/${id}`, send('PATCH', p)).then((r) => j<GroupRecord>(r)),
  removeGroup: (id: string) => fetch(`/api/agents/groups/${id}`, send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
  groupChats: (id: string) => fetch(`/api/agents/groups/${id}/chats`).then((r) => j<ChatInfo[]>(r)),

  temp: (ws?: string | null) => fetch(`/api/agents/temp${q({ ws })}`).then((r) => j<ChatInfo[]>(r)),
  setTempWith: (chatId: string, ids: string[]) => fetch(`/api/agents/temp/${chatId}`, send('PATCH', { with: ids })).then((r) => j<{ ok: boolean; with: string[] }>(r)),
  setUnread: (chatId: string, on: boolean) => fetch(`/api/agents/chats/${chatId}/unread`, send('POST', { on })).then((r) => j<{ ok: boolean }>(r)),

  addWorkspace: (w: { name: string; tint: string; brokers?: boolean }) => fetch('/api/agents/workspaces', send('POST', w)).then((r) => j<WorkspaceRecord>(r)),
  patchWorkspace: (id: string, p: { name?: string; tint?: string; brokers?: boolean }) => fetch(`/api/agents/workspaces/${id}`, send('PATCH', p)).then((r) => j<{ ok: boolean }>(r)),
  removeWorkspace: (id: string) => fetch(`/api/agents/workspaces/${id}`, send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
}

/** A conversation in pieces: the newest messages first, older ones when the reader scrolls up to them. */
export const chatPage = (chatId: string, before?: number | null, limit = 40) => fetch(`/api/chats/${chatId}${q({ limit, before })}`).then((r) => j<{ chat: Chat; messages: ChatMessage[]; more: boolean }>(r))

export const memoryApi = {
  patch: (id: string, p: { label?: string; value?: unknown; kind?: MemoryKind; off?: boolean }) => fetch(`/api/memory/${id}`, send('PATCH', p)).then((r) => j<Memory>(r)),
}

export interface SendBody {
  chatId?: string
  message: string
  attachments?: Attachment[]
  liveSearch?: boolean
  skillIds?: string[]
  mcpIds?: string[]
  mode?: ChatMode
  model?: string
  trading?: boolean
  /** A new conversation is somebody's: an agent's, a group chat's, or a temporary chat with these agents. */
  agentId?: string
  groupId?: string
  temp?: boolean
  tempWith?: string[]
  workspaceId?: string
}

async function readStream(body: ReadableStream<Uint8Array>, onEvent: (e: ChatStreamEvent) => void): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let sep: number
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, sep)
        buf = buf.slice(sep + 2)
        const data = frame
          .split('\n')
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).replace(/^ /, ''))
          .join('\n')
        if (!data) continue
        try {
          onEvent(JSON.parse(data) as ChatStreamEvent)
        } catch {
          /* a broken frame is skipped */
        }
      }
    }
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') onEvent({ type: 'error', message: (err as Error)?.message ?? 'The connection dropped' })
  } finally {
    reader.releaseLock()
  }
}

/** Send a message and read the answer as it is written. Resolves when the stream closes. */
export async function sendChat(body: SendBody, onEvent: (e: ChatStreamEvent) => void, signal?: AbortSignal): Promise<void> {
  let res: Response
  try {
    res = await fetch('/api/chat', { ...send('POST', body), signal })
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') onEvent({ type: 'error', message: 'The server could not be reached' })
    return
  }
  if (res.status === 401) window.dispatchEvent(new CustomEvent('lb-unauthorized'))
  if (!res.ok || !res.body) return onEvent({ type: 'error', message: `The server answered ${res.status}` })
  await readStream(res.body, onEvent)
}

/** Join an answer that is being written right now (after a reload, or one an automation started). */
export async function joinChat(chatId: string, onEvent: (e: ChatStreamEvent) => void, signal?: AbortSignal): Promise<void> {
  let res: Response
  try {
    res = await fetch(`/api/chat/${chatId}/stream`, { signal })
  } catch {
    return
  }
  if (!res.ok || !res.body) return
  await readStream(res.body, onEvent)
}
export const stopChat = (chatId: string) => fetch(`/api/chat/${chatId}/stop`, { method: 'POST' }).catch(() => {})

/* ── custom connectors: MCP servers the user added ───────────────────── */

export interface ConnectorTool {
  name: string
  description: string
}
export interface Connector {
  id: string
  label: string
  url: string
  description: string
  /** How the server knows the user: nothing, a token sent with every request, or a sign-in at the server itself. */
  auth: 'none' | 'token' | 'oauth'
  /** active = usable; pending = a sign-in was started and not finished; needs_login = the permission ran out. */
  status: 'active' | 'pending' | 'needs_login'
  enabled: boolean
  tools: ConnectorTool[]
  /** The tools agents may use; empty = all of them. */
  allowedTools: string[]
  lastError: string | null
}
export type Probe = { kind: 'tools'; tools: ConnectorTool[] } | { kind: 'signin' } | { kind: 'error'; message: string }

export const connectorsApi = {
  list: () => fetch('/api/connectors').then((r) => j<Connector[]>(r)),
  probe: (url: string, token?: string) => fetch('/api/connectors/probe', send('POST', { url, token })).then((r) => j<Probe>(r)),
  create: (c: { label: string; url: string; description?: string; token?: string; allowedTools?: string[] }) => fetch('/api/connectors', send('POST', c)).then((r) => j<Connector>(r)),
  patch: (id: string, p: Partial<Pick<Connector, 'label' | 'description' | 'enabled' | 'allowedTools'>>) => fetch(`/api/connectors/${id}`, send('PATCH', p)).then((r) => j<Connector>(r)),
  remove: (id: string) => fetch(`/api/connectors/${id}`, send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
  refresh: (id: string) => fetch(`/api/connectors/${id}/refresh`, send('POST')).then((r) => j<Connector>(r)),
  /** Start a sign-in for a new server: the answer is the server's own page to send the user to. */
  startSignIn: (c: { label: string; url: string; description?: string }) => fetch('/api/connectors/signin', send('POST', c)).then((r) => j<{ url: string }>(r)),
  /** Sign in again at a server already added. */
  signIn: (id: string) => fetch(`/api/connectors/${id}/signin`, send('POST')).then((r) => j<{ url: string }>(r)),
}

/** What is left of the week's allowance on the user's own AI plan. `known` false = the plan does not say. */
export interface Allowance {
  known: boolean
  percentLeft?: number
  resetsAt?: number | null
  period?: 'weekly' | 'monthly' | null
  plan?: string | null
  /** Why it is not known ("not connected", "the plan did not answer"). */
  note?: string
}
export const allowanceApi = () => fetch('/api/xai-oauth/allowance').then((r) => j<Allowance>(r))

/* ── invitations ─────────────────────────────────────────────────────── */

export interface Invite {
  token: string
  url: string
  createdAt: number
  expiresAt: number
  usedBy: string | null
  usedAt: number | null
}
export const invitesApi = {
  list: () => fetch('/api/invites').then((r) => j<{ invites: Invite[]; people: { id: string; name: string; joinedAt: number }[] }>(r)),
  create: () => fetch('/api/invites', send('POST')).then((r) => j<Invite>(r)),
  revoke: (token: string) => fetch(`/api/invites/${token}`, send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
  check: (token: string) => fetch(`/api/auth/invite/${token}`).then((r) => j<{ ok: boolean; from: string }>(r)),
  terms: () => fetch('/api/auth/terms').then((r) => j<{ version: string; text: string }>(r)),
  join: (b: { token: string; username: string; password: string; name?: string; terms: string }) => fetch('/api/auth/join', send('POST', b)).then((r) => j<{ user: unknown }>(r)),
}

/* ── copy trading ────────────────────────────────────────────────────── */

import type { CopyConsent, CopyDetail, CopyIntentView, CopyOverview, CopyPerson, CopyPoint, CopySettings, CopyView } from '@shared/copy'

export const copyApi = {
  overview: () => fetch('/api/copy').then((r) => j<CopyOverview>(r)),
  consent: () => fetch('/api/copy/consent').then((r) => j<CopyConsent>(r)),
  start: (b: { leaderId: string; amount: number; settings: CopySettings }) => fetch('/api/copy', send('POST', { ...b, consent: true })).then((r) => j<CopyView>(r)),
  detail: (id: string) => fetch(`/api/copy/${id}`).then((r) => j<CopyDetail>(r)),
  settings: (id: string, settings: Partial<CopySettings>) => fetch(`/api/copy/${id}`, send('PATCH', { settings })).then((r) => j<CopyView>(r)),
  deposit: (id: string, amount: number) => fetch(`/api/copy/${id}/deposit`, send('POST', { amount })).then((r) => j<CopyView>(r)),
  withdraw: (id: string, amount: number) => fetch(`/api/copy/${id}/withdraw`, send('POST', { amount })).then((r) => j<CopyView>(r)),
  pause: (id: string) => fetch(`/api/copy/${id}/pause`, send('POST')).then((r) => j<CopyView>(r)),
  resume: (id: string) => fetch(`/api/copy/${id}/resume`, send('POST')).then((r) => j<CopyView>(r)),
  sell: (id: string, symbol: string, fraction = 1) => fetch(`/api/copy/${id}/sell`, send('POST', { symbol, fraction })).then((r) => j<CopyIntentView>(r)),
  /** A stock sold out of the copy by hand is copied again from their next buy of it. */
  copyAgain: (id: string, symbol: string) => fetch(`/api/copy/${id}/sitting/${encodeURIComponent(symbol)}`, send('DELETE')).then((r) => j<CopyView>(r)),
  stop: (id: string, mode: 'sell' | 'keep') => fetch(`/api/copy/${id}/stop`, send('POST', { mode })).then((r) => j<CopyView>(r)),
  /** What the copy was worth over time. */
  history: (id: string) => fetch(`/api/copy/${id}/history`).then((r) => j<CopyPoint[]>(r)),
  /** Whom I can invite to copy me (whoever runs the server sees everyone). */
  people: () => fetch('/api/copy/people').then((r) => j<CopyPerson[]>(r)),
  /** What I accept before I invite someone to copy me (its version goes with the invitation). */
  leaderTerms: () => fetch('/api/copy/consent/leader').then((r) => j<CopyConsent>(r)),
  invite: (who: { userId?: string; username?: string }, terms: string) => fetch('/api/copy/grants', send('POST', { ...who, terms })).then((r) => j<CopyPerson>(r)),
  uninvite: (userId: string) => fetch(`/api/copy/grants/${userId}`, send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
  /** Another server: make a one-time code for someone who runs their own installation (the terms of being copied go with it). */
  remoteInvite: (terms: string) => fetch('/api/copy/remote/invites', send('POST', { terms })).then((r) => j<{ code: string; expiresAt: number }>(r)),
  /** Another server: take back the codes I made that nobody has used yet. */
  remoteCancelInvites: () => fetch('/api/copy/remote/invites', send('DELETE')).then((r) => j<{ cancelled: number }>(r)),
  /** Another server: enter the code its owner gave me — my server links to theirs. */
  remoteLink: (code: string) => fetch('/api/copy/remote/links', send('POST', { code })).then((r) => j<{ id: string; name: string; host: string }>(r)),
  remoteUnlink: (id: string) => fetch(`/api/copy/remote/links/${encodeURIComponent(id)}`, send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
  /** Someone on another server who is linked to me: take the link back (off), give it back (active) or remove it (gone). */
  remotePeer: (id: string, status: 'off' | 'active' | 'gone') => fetch(`/api/copy/remote/peers/${encodeURIComponent(id)}`, send('POST', { status })).then((r) => j<{ ok: boolean }>(r)),
  /** The server's own switch (admin): are follower orders really sent, or only worked out and written down? */
  mode: () => fetch('/api/copy/admin').then((r) => j<{ live: boolean }>(r)),
  setMode: (live: boolean) => fetch('/api/copy/admin', send('PUT', { live })).then((r) => j<{ live: boolean }>(r)),
}

/** What you are called here: on the screen, and by your agents. */
export const renameMe = (name: string) => fetch('/api/auth/me', send('PATCH', { name })).then((r) => j<{ user: { id: string; username: string; name: string | null; role: string } }>(r))

/** A key as the screens know it: set or not, where it came from, its last four characters — never the key. */
export interface KeyInfo {
  set: boolean
  source: 'app' | 'env' | null
  tail: string | null
}
export interface KeysState {
  xai: { own: KeyInfo; /** The server has a key of its own (it answers for whoever runs the server). */ server: boolean }
  eodhd: KeyInfo & { /** I am the one who may set it. */ mine: boolean }
}
export interface ServerOptions {
  options: { searchWeb: boolean; xImage: boolean; xVideo: boolean; /** Days a conversation's untouched working files are kept (0 = for ever). */ scratchDays: number }
  brave: KeyInfo
}
/** API keys entered in the app: a person's own xAI key, and the installation's market-data key. */
export const keysApi = {
  state: () => fetch('/api/keys').then((r) => j<KeysState>(r)),
  setXai: (key: string) => fetch('/api/keys/xai', send('PUT', { key })).then((r) => j<{ ok: boolean }>(r)),
  clearXai: () => fetch('/api/keys/xai', send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
  setEodhd: (key: string) => fetch('/api/keys/eodhd', send('PUT', { key })).then((r) => j<{ ok: boolean }>(r)),
  /** The installation's general options (whoever runs the server). */
  options: () => fetch('/api/keys/options').then((r) => j<ServerOptions>(r)),
  setOptions: (patch: Partial<ServerOptions['options']>) => fetch('/api/keys/options', send('PUT', patch)).then((r) => j<ServerOptions>(r)),
  setBrave: (key: string) => fetch('/api/keys/brave', send('PUT', { key })).then((r) => j<{ ok: boolean }>(r)),
  clearBrave: () => fetch('/api/keys/brave', send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
  clearEodhd: () => fetch('/api/keys/eodhd', send('DELETE')).then((r) => j<{ ok: boolean }>(r)),
}
